// EmailTemplateSelection.jsx – original card/grid UI with single *global* search bar
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "../../../styles/icons/RefreshIcon";

export default function EmailTemplateSelection({
  open,
  setSelected,
  setClosed,
  templates,
  refreshTemplates,
}) {
  const COLOR_SURFACE = "#2C2B2D";
  const COLOR_HEADER = "#1E1E1E";
  const COLOR_ACCENT = "#0DA3E2";
  const SEARCH_ICON_BG =
    "url(\"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15.7549 14.255H14.9649L14.6849 13.985C15.6649 12.845 16.2549 11.365 16.2549 9.755C16.2549 6.165 13.3449 3.255 9.75488 3.255C6.16488 3.255 3.25488 6.165 3.25488 9.755C3.25488 13.345 6.16488 16.255 9.75488 16.255C11.3649 16.255 12.8449 15.665 13.9849 14.685L14.2549 14.965V15.755L19.2549 20.745L20.7449 19.255L15.7549 14.255ZM9.75488 14.255C7.26488 14.255 5.25488 12.245 5.25488 9.755C5.25488 7.26501 7.26488 5.255 9.75488 5.255C12.2449 5.255 14.2549 7.26501 14.2549 9.755C14.2549 12.245 12.2449 14.255 9.75488 14.255Z' fill='white'/%3E%3C/svg%3E\")";

  /** ------------------------------------------------------------------
   * Global search state & logic
   * ------------------------------------------------------------------ */
  const [query, setQuery] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [temp, setTemp] = useState(null);

  const fallbackImageSrc = "/path/to/fallback.png";

  useEffect(() => {
    if (!query.trim()) {
      setFilteredItems(templates);
      return;
    }

    const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);

    const matchesAllTokens = (item) => {
      const haystacks = [
        item.Id?.toString().toLowerCase() ?? "",
        item.CampId?.toString().toLowerCase() ?? "",
        item.Name?.toLowerCase() ?? "",
        item.CampName?.toLowerCase() ?? "",
        (item.Content ?? item.HtmlBody ?? "").toLowerCase(),
        (item.Description ?? "").toLowerCase(),
      ];
      return tokens.every((token) => haystacks.some((h) => h.includes(token)));
    };

    setFilteredItems(templates.filter(matchesAllTokens));
  }, [query, templates]);

  const handleSubmit = () => {
    if (temp) {
      setSelected(temp);
      setClosed();
    }
  };

  return (
    <Dialog
      open={open}
      sx={{
        "& .MuiDialog-paper": {
          width: "905px",
          maxWidth: "905px",
          height: "829px",
          maxHeight: "829px",
          borderRadius: "7px",
          border: "1px solid rgba(255, 255, 255, 0.45)",
          bgcolor: COLOR_SURFACE,
          color: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {/* Sticky Search Bar */}
      <Box
        sx={{
          px: "20px",
          py: "10px",
          borderBottom: "1px solid rgba(173, 180, 189, 0.2)",
          bgcolor: COLOR_HEADER,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <TextField
          fullWidth
          size="small"
          label="Search"
          placeholder="templates / name / content"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Box
                  component="span"
                  sx={{
                    width: 24,
                    height: 24,
                    display: "inline-block",
                    backgroundImage: SEARCH_ICON_BG,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "contain",
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 40,
              color: "#FFFFFF",
              bgcolor: COLOR_HEADER,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(173, 180, 189, 0.45)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: COLOR_ACCENT,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: COLOR_ACCENT,
              },
            },
            "& .MuiInputLabel-root": {
              color: "rgba(255,255,255,0.7)",
              fontSize: "12px",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: COLOR_ACCENT,
            },
          }}
        />
        <IconButton
          aria-label="Refresh"
          onClick={refreshTemplates}
          sx={{
            color: "#FFFFFF",
          }}
        >
          <RefreshIcon sx={{ color: "#FFFFFF" }} />
        </IconButton>
      </Box>

      {/* Scrollable Card Grid */}
      <Box
        sx={{
          flexGrow: 1,
          bgcolor: COLOR_SURFACE,
          overflowY: "auto",
          p: "14px 18px",
          display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: "12px",
          alignContent: "start",
          gridAutoRows: "1fr",
        }}
      >
        {filteredItems.length ? (
          filteredItems.map((item) => (
            <Box
              key={item.Id}
              onClick={() => setTemp(item)}
              sx={{
                display: "flex",
                flexDirection: "column",
                border:
                  temp?.Id === item.Id
                    ? `2px solid ${COLOR_ACCENT}`
                    : "1px solid rgba(173, 180, 189, 0.35)",
                borderRadius: "6px",
                bgcolor: COLOR_HEADER,
                minHeight: 182,
                width: "100%",
                cursor: "pointer",
                overflow: "hidden",
                position: "relative",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: COLOR_ACCENT,
                },
              }}
            >
              <Box
                sx={{
                  minHeight: 30,
                  maxHeight: 30,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1,
                  borderBottom: "1px solid rgba(173, 180, 189, 0.2)",
                  flexShrink: 0,
                  bgcolor: COLOR_HEADER,
                }}
              >
                <Tooltip
                  title={`${item.CampName || item.Name || ""} (CampId: ${item.CampId ?? "—"})`}
                  placement="top"
                >
                  <Typography
                    sx={{
                      color: "#FFFFFF",
                      fontSize: "12px",
                      fontWeight: 700,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "180px",
                    }}
                  >
                    {item.CampName || item.Name}
                    {item.CampId != null && (
                      <Box
                        component="span"
                        sx={{
                          color: "rgba(255,255,255,0.55)",
                          fontWeight: 500,
                          ml: 0.5,
                        }}
                      >
                        #{item.CampId}
                      </Box>
                    )}
                  </Typography>
                </Tooltip>
                {temp?.Id === item.Id && (
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: COLOR_ACCENT,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 10,
                        height: 8,
                        backgroundImage:
                          "url(\"data:image/svg+xml,%3Csvg width='10' height='8' viewBox='0 0 10 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 3.66667L3.66667 6.33333L9 1' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                        backgroundSize: "contain",
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* Image Preview with fallback */}
              <Box
                component="img"
                src={item.PreviewUrl}
                alt={item.Name}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = fallbackImageSrc;
                }}
                sx={{
                  flexGrow: 1,
                  width: "100%",
                  objectFit: "contain",
                  bgcolor: COLOR_HEADER,
                }}
              />
            </Box>
          ))
        ) : (
          <Typography color="#FFFFFF">No Templates Found</Typography>
        )}
      </Box>

      {/* Footer Buttons */}
      <Box
        sx={{
          px: "20px",
          pb: "16px",
          pt: "20px",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          bgcolor: COLOR_SURFACE,
          borderTop: "1px solid rgba(173, 180, 189, 0.2)",
        }}
      >
        <Button
          variant="outlined"
          sx={{
            width: "187px",
            height: "50px",
            minWidth: "187px",
            borderRadius: "7px",
            textTransform: "none",
            borderColor: "#0DA3E2",
            color: "#FFFFFF",
            "&:hover": {
              borderColor: "#0DA3E2",
              bgcolor: "rgba(13, 163, 226, 0.08)",
            },
          }}
          onClick={setClosed}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!temp}
          sx={{
            width: "187px",
            height: "50px",
            minWidth: "187px",
            borderRadius: "7px",
            textTransform: "none",
            backgroundColor: "#0DA3E2",
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#0DA3E2",
              opacity: 0.92,
            },
          }}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Box>
    </Dialog>
  );
}
